import type { EnemyDef } from '../../content/enemies/enemies';
import type { CombatState, PlayerAttributes, PlayerState } from '../game/types';
import { resolveEnemyAttack } from './enemyAttack';
import { hasStatus } from './statuses';

export type PreCombatFleeResult =
  | { outcome: 'escaped'; chance: number; message: string }
  | { outcome: 'failed_enter_combat'; chance: number; message: string };

export type InCombatFleeResult =
  | { outcome: 'escaped'; chance: number; message: string }
  | {
      outcome: 'failed';
      chance: number;
      player: PlayerState;
      combat: CombatState;
      killed: boolean;
      message: string;
    }
  | { outcome: 'blocked'; message: string };

/** Base 50% flee success +2% per DEX, capped at 90%. */
export function computeFleeChance(stats: PlayerAttributes): number {
  return Math.min(0.9, 0.5 + stats.dexterity * 0.02);
}

export function rollFlee(stats: PlayerAttributes, rng: () => number = Math.random): boolean {
  return rng() < computeFleeChance(stats);
}

export function fleeChancePercent(stats: PlayerAttributes): number {
  return Math.round(computeFleeChance(stats) * 100);
}

export function fleeChanceLabel(stats: PlayerAttributes): string {
  return `Chạy trốn (~${fleeChancePercent(stats)}% · DEX)`;
}

export function resolvePreCombatFlee(
  stats: PlayerAttributes,
  enemyName: string,
  rng: () => number = Math.random,
): PreCombatFleeResult {
  const chance = computeFleeChance(stats);
  const percent = Math.round(chance * 100);

  return rollFlee(stats, rng)
    ? {
        outcome: 'escaped',
        chance,
        message: `🏃 Lảng tránh ${enemyName} thành công (${percent}% · DEX). Bạn rút về phòng trước.`,
      }
    : {
        outcome: 'failed_enter_combat',
        chance,
        message: `🏃 Chạy trốn thất bại (${percent}% · DEX)! ${enemyName} chặn đường — chiến đấu bắt đầu. Bạn ra tay trước.`,
      };
}

export function resolveInCombatFlee(
  player: PlayerState,
  combat: CombatState,
  enemy: Pick<EnemyDef, 'accuracy' | 'name'> = {
    accuracy: combat.enemyAccuracy,
    name: combat.enemyName,
  },
  rng: () => number = Math.random,
): InCombatFleeResult {
  if (hasStatus(combat.playerStatuses, 'fear')) {
    return { outcome: 'blocked', message: '😨 Hoảng sợ — không thể chạy trốn!' };
  }

  const chance = computeFleeChance(player.stats);
  const percent = Math.round(chance * 100);
  if (rollFlee(player.stats, rng)) {
    return {
      outcome: 'escaped',
      chance,
      message: `🏃 Thoát khỏi ${combat.enemyName} giữa trận (${percent}% · DEX). Bạn giữ vị trí và có thể đi tiếp.`,
    };
  }

  const hit = resolveEnemyAttack(player, combat, enemy, rng);
  const failLine = `🏃 Chạy trốn thất bại (${percent}% · DEX)!`;
  return {
    outcome: 'failed',
    chance,
    player: hit.player,
    combat: {
      ...hit.combat,
      log: [failLine, ...hit.combat.log].slice(0, 8),
      playerDefeated: hit.killed ? true : hit.combat.playerDefeated,
    },
    killed: hit.killed,
    message: hit.killed
      ? `${failLine} ${combat.enemyName} đánh trúng — bạn gục xuống.`
      : `${failLine} Bạn vẫn sống sót sau đòn phản công.`,
  };
}
